import { createIdentifier } from '@blocksuite/global/di';
import type { ExtensionType } from '@blocksuite/store';
import { html, type TemplateResult } from 'lit';
import { until } from 'lit/directives/until.js';

export type UserAvatarCacheRequest = {
  userId: string;
  avatarUrl: string;
};

export type UserAvatarCacheResolver = (
  request: UserAvatarCacheRequest
) => Promise<string | null | undefined>;

export interface UserAvatarCacheService {
  resolveCachedUserAvatarUrl: UserAvatarCacheResolver;
}

export const UserAvatarCacheProvider = createIdentifier<UserAvatarCacheService>(
  'streamlain-user-avatar-cache-service'
);

export function UserAvatarCacheServiceExtension(
  service: UserAvatarCacheService
): ExtensionType {
  return {
    setup(di) {
      di.addImpl(UserAvatarCacheProvider, () => service);
    },
  };
}

export function revokeCachedUserAvatarObjectUrl(url: string, cached: boolean) {
  if (cached) {
    URL.revokeObjectURL(url);
  }
}

export function renderCachedUserAvatarUrl({
  avatarCache,
  avatarUrl,
  render,
  userId,
}: UserAvatarCacheRequest & {
  avatarCache: UserAvatarCacheService | null | undefined;
  render: (url: string, cached: boolean) => TemplateResult;
}) {
  const live = render(avatarUrl, false);
  if (!avatarCache) {
    return live;
  }

  return html`${until(
    avatarCache
      .resolveCachedUserAvatarUrl({ userId, avatarUrl })
      .then(cachedUrl => (cachedUrl ? render(cachedUrl, true) : live))
      .catch(() => live),
    live
  )}`;
}
